<template lang="pug">
    //- v-card.mx-lg-auto.mt-md-4(outlined)
    v-card-text
        v-container
            v-row
                v-col(cols='12')
                    v-text-field(
                        v-model="obj.title"
                        label='Title *'
                        type='text'
                    )

                v-col(cols='12')
                    | Description
                    tiptap-vuetify(
                        v-model='obj.description'
                        :extensions='extensions'
                        :toolbar-attributes="{ color: 'var(--volie-blue)', dark: true }"
                        style=""
                    )

                v-col(cols='12' v-if="currentUserIsSuperAdmin && !validTicket")
                    v-autocomplete(
                        v-model='obj.account_id'
                        :items='accounts'
                        label='Account'
                        item-text="name"
                        item-value="account_id"
                    )

                v-col(cols='12' v-if="!disableAssignables")
                    v-autocomplete(
                        v-model='obj.assigned_to'
                        label='Assigned To*'
                        type='text'
                        :items="assignables"
                        item-text="key"
                        item-value="value"
                        clearable
                    )

                v-col(cols='6')
                    v-autocomplete(
                        v-model='obj.ticket_type'
                        label='Type *'
                        :items='types'
                        item-text='key'
                        item-value='value'
                    )
                v-col(cols='6')
                    v-autocomplete(
                        v-model='obj.ticket_category_id'
                        label='Category*'
                        :items='categories'
                        item-text='name'
                        item-value='id'
                    )
                v-col(cols='12')
                    v-text-field(
                        v-model='obj.affected_app_page'
                        label='Affected App Page'
                        type='text'
                        placeholder='e.g.: "/campaigns", "/communication_center", "/dashboards/live-dashboard"'
                    )


                template(v-if="validTicket")

                    template(v-if="currentUserIsSuperAdmin")
                        v-col(cols='6')
                            v-autocomplete(
                                v-model='obj.current_status'
                                label='Status *'
                                :items='statuses'
                                item-text='key'
                                item-value='value'
                            )

                        v-col(cols='6')
                            v-autocomplete(
                                v-model='obj.completion_code'
                                label='Completion Code *'
                                :items='completionCodes'
                                item-text='key'
                                item-value='value'
                                :disabled="obj.current_status != 'done'"
                            )
                        v-col(cols='6')
                            v-autocomplete(
                                v-model='obj.internal_development_priority'
                                label='Internal Priority'
                                :items='internalDevelopmentPriorities'
                                item-text='key'
                                item-value='value'
                            )

                        v-col(cols='6')
                            v-autocomplete(
                                v-model='obj.internal_development_stage'
                                label='Development Stage'
                                :items='shownStages'
                                item-text='key'
                                item-value='value'
                            )
                        v-col(cols='12')
                            v-autocomplete(
                                v-model='obj.flag_ids'
                                label='Flag(s)'
                                :items='ticketFlags'
                                item-text='name'
                                item-value='id'
                                multiple
                                clearable
                            )

                    v-col(cols='12')
                        | Add Comment
                        tiptap-vuetify(
                            v-model='obj.comments'
                            :extensions='extensions'
                            :toolbar-attributes="{ color: 'var(--volie-blue)', dark: true }"
                        )

                v-col(cols='12')
                    Asset(
                        :account-id="currentUser.activated_account_id"
                        :loading-ticket-update="isSaving"
                        :show-snackbar="showSnackbar"
                        :ticket-id="validTicket ? ticket.ticket_id : null"
                        :updated-assets="onUpdatedAssets"
                        @create-assets-form-data="createAssetsFormData"
                    )
                    v-alert(
                        v-if="!validTicket && assetFormData"
                        type="success"
                        dense
                        text
                        class="mt-2"
                    )
                        v-icon(slot="prepend" color="success") mdi-check-circle
                        | Attachment(s) uploaded successfully

            v-col(cols='12' style="padding-left: 0px; padding-right: 0px;")
                .text-right
                    v-btn.ma-2(v-if="!validTicket" outlined ripple :to="{ name: 'ticket_index' }")
                        v-icon mdi-window-close
                        | Cancel

                    v-btn.ma-2.white--text(v-if="!validTicket" :loading="isSaving" :disabled="!eligibleToSave" ripple color="green" @click="createTicket()")
                        v-icon mdi-content-save
                        | Create
                    
                    .inline-admin-only(v-if="validTicket && currentUserIsSuperAdmin")
                        HelpMenu(
                            :help-definitions="helpDefinitions"
                            excludeTitle
                        )

                        v-checkbox.mr-4(
                            v-model="obj.super_admin_activity"
                            label="Admin Only"
                            hide-details
                        )
                            template(v-slot:label)
                                span Admin Only

                    v-btn.ma-2.white--text(v-if="validTicket" :loading="isSaving" :disabled="!eligibleToSave" ripple color="green" @click="updateTicket()")
                        v-icon mdi-content-save
                        | Update
                    
                    ReopenTicketDialog(
                        v-if="validTicket && ticket.current_status === 'done'"
                        :reload-ticket="reloadTicket"
                        :show-snackbar="showSnackbar"
                        :ticket="ticket"
                    )

</template>

<script lang="ts">
    import Vue from 'vue';
    import VolieState from '@/store';

    // Data
    import {
        CreateAssets,
        CreateTicket,
        GetAccounts,
        GetTicketFlags,
        GetTicketCategories,
        UpdateTicket,
        EscalateTicketToEngineering,
    } from "@/data";
    
    // Components
    import Asset from '@/components/tickets/partials/asset.vue';
    import HelpMenu from '@/components/reports/help_menu.vue';
    import ReopenTicketDialog from '@/components/tickets/reopen_ticket_dialog.vue';
    import {
        TiptapVuetify,
        Heading,
        Bold,
        Italic,
        Strike,
        Underline,
        // Code,
        Paragraph,
        BulletList,
        OrderedList,
        ListItem,
        Link,
        Blockquote,
        HardBreak,
        // HorizontalRule,
        // History,
    } from 'tiptap-vuetify';

    export default Vue.extend({
        data() {
            return {
                accounts: [],
                assignables: [
                    { key: "Volie Support Team", value: "support_team" },
                    { key: "Volie Customer Success Coordinator", value: "customer_success_coordinator" },
                    { key: "Volie Customer Success Coach", value: "customer_success_coach" },
                ],
                categories: [],
                checkDigit: (event: KeyboardEvent) => {
                    if (event.key.length === 1 && isNaN(Number(event.key))) {
                        event.preventDefault();
                    }},
                completionCodes: [
                    { key: "Completed", value: "completed" },
                    { key: "Duplicate", value: "duplicate" },
                    { key: "Not Able To Reproduce", value: "not_able_to_reproduce" },
                    { key: "Not Going To Do", value: "not_going_to_do" },
                ],
                assetFormData: null,
                extensions: [
                    Blockquote,
                    Link,
                    Underline,
                    Strike,
                    Italic,
                    ListItem,
                    BulletList,
                    OrderedList,
                    [Heading, {
                        options: {
                            levels: [1, 2, 3]
                        }
                    }],
                    Bold,
                    Paragraph,
                    HardBreak
                ],
                helpDefinitions: [
                    {
                        title: "Admin Only",
                        description: "When this box is checked, updates made to this ticket will only be visible to admins."
                    }
                ],
                isSaving: false,
                internalDevelopmentPriorities: [
                    { key: "Critical", value: "critical" },
                    { key: "High", value: "high" },
                    { key: "Medium", value: "medium" },
                    { key: "Low", value: "low" },
                ],
                obj: {
                    super_admin_activity: false,
                },
                priorities: [
                    { key: "Critical", value: "critical" },
                    { key: "High", value: "high" },
                    { key: "Low", value: "low" },
                ],
                savedStage: "",
                shownStages: [],
                // key: name displayed in dropdown
                // value: enum value used in the backend
                // moveable: all stages a particular stage can be changed into
                stages: [
                    { key: "Backlog",                   value: "backlog",                   moveable: [ 'awaiting_documentation' ],                                                             },
                    { key: "Awaiting Documentation",    value: "awaiting_documentation",    moveable: [ 'awaiting_estimate', 'backlog' ],                                                       },
                    { key: "Awaiting Estimate",         value: "awaiting_estimate",         moveable: [ 'on_deck', 'awaiting_documentation' ],                                                  },
                    { key: "On Deck",                   value: "on_deck",                   moveable: [ 'development_in_progress', 'awaiting_estimate', 'awaiting_documentation' ],             },
                    { key: "Development in Progress",   value: "development_in_progress",   moveable: [ 'dev_blocked', 'dev_paused', 'dev_qa' ],                                                },
                    { key: "Dev Blocked",               value: "dev_blocked",               moveable: [ 'dev_paused', 'development_in_progress', 'dev_qa', 'technical_rework' ],                },
                    { key: "Dev Paused",                value: "dev_paused",                moveable: [ 'dev_blocked', 'development_in_progress', 'dev_qa', 'technical_rework' ],               },
                    { key: "Dev QA",                    value: "dev_qa",                    moveable: [ 'awaiting_code_review', 'dev_blocked', 'dev_paused' ],                                  },
                    { key: "Awaiting Code Review",      value: "awaiting_code_review",      moveable: [ 'technical_rework', 'awaiting_code_merge' ],                                            },
                    { key: "Technical Rework",          value: "technical_rework",          moveable: [ 'awaiting_code_merge', 'dev_blocked', 'dev_paused', 'awaiting_code_review' ],           },
                    { key: "Awaiting Code Merge",       value: "awaiting_code_merge",       moveable: [ 'merged', 'technical_rework' ],                                                         },
                    { key: "Merged",                    value: "merged",                    moveable: [ 'awaiting_deploy' ],                                                                    },
                    { key: "Awaiting Deploy",           value: "awaiting_deploy",           moveable: [ 'in_production' ],                                                                      },
                    { key: "In Production",             value: "in_production",             moveable: [ ],                                                                                      },
                ],
                statuses: [
                    { key: "Done", value: "done" },
                    { key: "In Review", value: "in_review" },
                    { key: "In Progress", value: "in_progress" },
                    { key: "Open", value: "open" },
                ],
                ticketFlags: [],
                types: [
                    { key: "Bug", value: "bug" },
                    { key: "Enhancement", value: "enhancement" },
                    { key: "Question", value: "question" },
                ],
            }
        },
        methods: {
            checkAbleToUpdate() {
                // shorthand variable
                const currentStage = this.obj.internal_development_stage;
                // if the stage isn't null and the previous stage isn't null and they aren't equal
                if (currentStage !== null && this.savedStage !== null && currentStage !== this.savedStage) {
                    // find the current stage's associated object
                    const currentStageObject = this.stages.find(obj => obj.value === currentStage);
                    // same for the previous stage
                    const savedStageObject = this.stages.find(obj => obj.value === this.savedStage);

                    // if you cannot move from the previous stage to the current stage
                    if (!savedStageObject?.moveable?.includes(currentStage)) {
                        // get the stages you are allowed to move to
                        const allowedObjects = this.stages.filter(obj => savedStageObject.moveable.includes(obj.value));
                        // get all the friendly names of the objects
                        const friendlyNames = allowedObjects.map(obj => obj.key);
                        // concat the names
                        const showString = friendlyNames.join(', ');

                        this.showSnackbar("error", "Unable to transition from " + savedStageObject.key + " to " + currentStageObject.key + ".\bYou are able to move the stage to any of the following: " + showString + ".");
                        return false;
                    }
                }

                return true;
            },
            createTicket() {

                this.obj.current_status = 'open';
                this.obj.current_user_id = this.currentUser?.user_id || 0;
                this.obj.assigned_to = this.obj.assigned_to || 'support_team';
                this.obj.affected_app_page = this.obj.affected_app_page || '';

                if (!(this.currentUserIsSuperAdmin)) {
                    this.obj.account_id = this.currentUser?.account?.account_id;
                }

                this.isSaving = true;
                CreateTicket(this.obj).then((response) => {
                    if (response?.Error === "Ticketing is not available when Intercom is enabled") {
                        this.$router.push({ name: 'root' });
                        return;
                    }

                    if (response?.Error === null) {
                        this.showSnackbar("success", "Successfully created ticket");
                        this.createAssets(response.Data.ticket_id);
                        setTimeout(() => { this.$router.push({ name: 'ticket_index' }) }, 1500);
                    } else {
                        let err = response?.Error;
                        if (!(err?.length > 0)) {
                            err = "Unable to create ticket"
                        }
                        this.showSnackbar("error", err);
                    }

                    this.isSaving = false;
                });
            },
            createAssets(ticketId) {

                if (!this.assetFormData) {
                    return;
                }
                // Set ticket id
                this.assetFormData.set("asset_id", ticketId);
                this.assetFormData.set("create_ticket_activity_media", true);

                CreateAssets(this.assetFormData).then((response) => {

                    if (!(response.Data?.assets?.length > 0)) {
                        this.showSnackbar("error", "Unable to create new asset");
                    }

                });
            },
            createAssetsFormData(formData) {
                this.assetFormData = formData;
            },
            getAccounts() {
                GetAccounts({ sort_by_column_name: "name" }).then((response) => {
                    if (response?.Data?.accounts?.length > 0) {
                        this.accounts = response.Data.accounts;
                    }
                });
            },
            getTicketCategories() {
                GetTicketCategories().then((response) => {
                    if (response?.Data?.categories?.length > 0) {
                        this.categories = response.Data.categories;
                    }
                });
            },
            getTicketFlags() {
                GetTicketFlags().then((response) => {
                    if (response?.Data?.flags?.length > 0) {
                        this.ticketFlags = response.Data.flags;
                    }
                });
            },
            initForm() {
                if (this.ticket) {
                    this.obj = { ...this.ticket };

                    this.savedStage = this.obj.internal_development_stage;
                }

                if (this.currentUserIsSuperAdmin && !this.validTicket) {
                    this.getAccounts();
                }

                // default to support team
                this.obj.assigned_to = this.obj.assigned_to || 'support_team';
            },
            onUpdatedAssets(assets) {
                const media = [];
                assets.forEach(asset => {
                    media.push({"mediaable_type": "asset", "mediaable_id": asset.id});
                });

                this.obj.media = media;
            },
            sanitizeDigits(digits) {
                digits = String(digits);
                // if digits is empty or is a negative number
                // default to 0
                if (digits.length === 0 || Number(digits) < 0) {
                    digits = '0';
                }
                // if there are 2 or more digits and 0 is the first digit
                // remove the 0
                if (digits.length > 1 && digits[0] === '0') {
                    digits = digits.substring(1);
                }
                return Number(digits);
            },
            updateTicket() {

                if (!this.checkAbleToUpdate()) {
                    return;
                }

                // initialize update options
                const options = {
                    affected_app_page: this.obj.affected_app_page || '',
                    assigned_to:     this.obj.assigned_to || '',
                    ticket_category_id:    this.obj.ticket_category_id || 0,
                    comments:        this.obj.comments || '',
                    completion_code: this.obj.completion_code || '',
                    current_status:  this.obj.current_status || '',
                    description:     this.obj.description || '',
                    flag_ids:        this.obj.flag_ids || [],
                    internal_development_priority:       this.obj.internal_development_priority || '',
                    internal_development_stage:          this.obj.internal_development_stage || '',
                    super_admin_activity:    this.obj.super_admin_activity || false,
                    media:           this.obj.media || [],
                    ticket_type:     this.obj.ticket_type || '',
                    title:           this.obj.title || '',
                }

                this.isSaving = true;
                UpdateTicket(this.ticket.ticket_key, options).then((response) => {
                    if (response?.Error === null) {
                        this.showSnackbar("success", "Successfully updated ticket");

                        this.reloadTicket();
                    } else {
                        let err = response?.Error;
                        if (!(err?.length > 0)) {
                            err = "Unable to update ticket"
                        }
                        this.showSnackbar("error", err);
                    }

                    this.isSaving = false;
                });
            },
            async escalateTicketToEngineering() {

                this.isSaving = true;

                const isOk = await EscalateTicketToEngineering(this.ticket.ticket_key).then((response) => {
                    if (response.Error !== null || response.Data === null || !(response.Data.jira_issue_id?.length > 0)) {
                        this.showSnackbar('error', 'Something went wrong Escalating the ticket to Engineering');

                        return false;
                    } else {

                        this.ticket.jira_issue_id = response.Data.jira_issue_id;
                        this.ticket.jira_issue_key = response.Data.jira_issue_key;

                        this.showSnackbar('success', 'Successfully Escalated the ticket to Engineering');
                    }

                    return true;
                })

                if (!isOk) {
                    this.isSaving = false;
                    return;
                }

                await this.reloadTicket();

                this.isSaving = false;
            }
        },
        created() {
            this.getTicketCategories();
            this.getTicketFlags();
            this.initForm();
        },
        components: {
            Asset,
            HelpMenu,
            ReopenTicketDialog,
            TiptapVuetify,
        },
        computed: {
            currentUser() {
                return VolieState.currentUser();
            },
            currentUserIsSuperAdmin() {
                return this.currentUser?.activated_role_id === 1;
            },
            disableAssignables() {
                return !this.currentUserIsSuperAdmin;
            },
            eligibleToSave() {
                if (!(this.obj?.title?.length > 0)) {
                    return false;
                }
                if (!(this.obj?.ticket_type?.length > 0)) {
                    return false;
                }
                if (!(this.obj?.assigned_to?.length > 0)) {
                    return false;
                }
                if (!(this.obj?.description?.length > 0)) {
                    return false;
                }

                // If its a new ticket, require the category
                if (!this.validTicket && !(this.obj?.ticket_category_id > 0)) {
                    return false;
                }

                return true;
            },
            validTicket() {
                return this.ticket?.ticket_id > 0;
            },
        },
        watch: {
            obj: function(value) {
                const newStage = value.internal_development_stage;

                if (newStage === null) {
                    this.shownStages = this.stages;

                    return;
                }

                const stageObject = this.stages.find(obj => obj.value === newStage);
                this.shownStages = [];

                for (let i = 0; i < this.stages.length; i++) {
                    if (this.stages[i].value === newStage || stageObject?.moveable?.includes(this.stages[i].value)) {
                        this.shownStages.push(this.stages[i]);
                    }
                }
            },
        },
        props: {
            reloadTicket: Function,
            showSnackbar: Function,
            ticket:       Object,
        }
    });
</script>

<style lang="scss" scoped> 
.inline-admin-only {
    display: inline-flex;
    vertical-align: middle;
    margin-bottom: 0px;
}
.v-input {
    margin: 0px;
    display: flex;
    align-items: center;

    .v-input__slot .v-label span {
        margin-top: 7px;
    }
}
</style>
